import { Amplify } from 'aws-amplify'
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updatePassword,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
  type UpdatePasswordInput,
} from 'aws-amplify/auth'
import { env } from '@/config/env'

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: env.COGNITO_POOL_ID,
      userPoolClientId: env.COGNITO_CLIENT_ID,
      signUpVerificationMethod: 'code',
      loginWith: {
        email: true,
      },
      passwordFormat: {
        minLength: 8,
        requireLowercase: true,
        requireUppercase: true,
        requireNumbers: true,
        requireSpecialCharacters: false,
      },
    },
  },
})

export interface CognitoUser {
  id: string
  email: string
  firstName: string
  lastName: string
  tenantId: string
  role: string
  emailVerified: boolean
}

/**
 * Sign in with email and password
 */
export async function cognitoSignIn(email: string, password: string) {
  const input: SignInInput = {
    username: email,
    password,
  }

  const result = await signIn(input)
  return result
}

/**
 * Sign up a new user
 */
export async function cognitoSignUp(
  email: string,
  password: string,
  attributes: {
    firstName: string
    lastName: string
    companyName: string
  }
) {
  const input: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        given_name: attributes.firstName,
        family_name: attributes.lastName,
        'custom:company_name': attributes.companyName,
      },
    },
  }

  const result = await signUp(input)
  return result
}

/**
 * Confirm sign up with verification code
 */
export async function cognitoConfirmSignUp(email: string, code: string) {
  const input: ConfirmSignUpInput = {
    username: email,
    confirmationCode: code,
  }

  const result = await confirmSignUp(input)
  return result
}

/**
 * Resend verification code
 */
export async function cognitoResendCode(email: string) {
  const result = await resendSignUpCode({ username: email })
  return result
}

/**
 * Sign out the current user
 */
export async function cognitoSignOut() {
  await signOut()
}

/**
 * Start password reset flow
 */
export async function cognitoResetPassword(email: string) {
  const input: ResetPasswordInput = {
    username: email,
  }

  const result = await resetPassword(input)
  return result
}

/**
 * Confirm password reset with code and new password
 */
export async function cognitoConfirmResetPassword(
  email: string,
  code: string,
  newPassword: string
) {
  const input: ConfirmResetPasswordInput = {
    username: email,
    confirmationCode: code,
    newPassword,
  }

  await confirmResetPassword(input)
}

/**
 * Change password for authenticated user
 */
export async function cognitoChangePassword(
  oldPassword: string,
  newPassword: string
) {
  const input: UpdatePasswordInput = {
    oldPassword,
    newPassword,
  }

  await updatePassword(input)
}

/**
 * Get the current authenticated user
 */
export async function cognitoGetCurrentUser(): Promise<CognitoUser | null> {
  try {
    const user = await getCurrentUser()
    const attributes = await fetchUserAttributes()

    return {
      id: user.userId,
      email: attributes.email || '',
      firstName: attributes.given_name || '',
      lastName: attributes.family_name || '',
      tenantId: attributes['custom:tenant_id'] || '',
      role: attributes['custom:role'] || 'viewer',
      emailVerified: attributes.email_verified === 'true',
    }
  } catch {
    return null
  }
}

/**
 * Get the current auth session (tokens)
 */
export async function cognitoGetSession() {
  try {
    const session = await fetchAuthSession()
    return session
  } catch {
    return null
  }
}

/**
 * Get the current access token
 */
export async function cognitoGetAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.accessToken?.toString() || null
  } catch {
    return null
  }
}

/**
 * Get the current ID token (contains user attributes)
 */
export async function cognitoGetIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || null
  } catch {
    return null
  }
}

/**
 * Check if user is authenticated
 */
export async function cognitoIsAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser()
    return true
  } catch {
    return false
  }
}

export {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updatePassword,
}
